'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Apartment {
  id: string;
  name: string;
  type: string;
  price: number;
  priceUSD: number;
  image: string;
  alt: string;
}

interface BookingSummaryProps {
  selectedApartment: Apartment | null;
  checkIn: string;
  checkOut: string;
  nights: number;
  currency: 'INR' | 'USD';
  onCurrencyChange: (currency: 'INR' | 'USD') => void;
}

export default function BookingSummary({
  selectedApartment,
  checkIn,
  checkOut,
  nights,
  currency,
  onCurrencyChange,
}: BookingSummaryProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-white rounded-xl shadow-elevated p-6 sticky top-24">
        <div className="h-64 bg-[var(--color-muted)] rounded-lg animate-pulse" />
      </div>
    );
  }

  const basePrice = selectedApartment
    ? currency === 'INR'
      ? selectedApartment.price
      : selectedApartment.priceUSD
    : 0;
  const subtotal = basePrice * nights;
  const tax = subtotal * 0.05;
  const total = subtotal + tax;

  const formatCurrency = (amount: number) => {
    if (currency === 'INR') {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
    return `$${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Select date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-white rounded-xl shadow-elevated p-6 sticky top-24">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-display font-semibold text-[var(--color-text-primary)]">
          Booking Summary
        </h3>
        <div className="flex items-center gap-2 bg-[var(--color-muted)] rounded-lg p-1">
          <button
            onClick={() => onCurrencyChange('INR')}
            className={`px-3 py-1 text-sm font-medium rounded transition-all duration-300 ${
              currency === 'INR' ?'bg-white text-[var(--color-primary)] shadow-subtle' :'text-[var(--color-text-secondary)]'
            }`}
          >
            ₹ INR
          </button>
          <button
            onClick={() => onCurrencyChange('USD')}
            className={`px-3 py-1 text-sm font-medium rounded transition-all duration-300 ${
              currency === 'USD' ?'bg-white text-[var(--color-primary)] shadow-subtle' :'text-[var(--color-text-secondary)]'
            }`}
          >
            $ USD
          </button>
        </div>
      </div>

      {selectedApartment ? (
        <>
          <div className="mb-6">
            <div className="relative h-32 rounded-lg overflow-hidden mb-3">
              <AppImage
                src={selectedApartment.image}
                alt={selectedApartment.alt}
                className="w-full h-full object-cover"
              />
            </div>
            <h4 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {selectedApartment.name}
            </h4>
            <p className="text-sm text-[var(--color-text-secondary)]">{selectedApartment.type}</p>
          </div>

          <div className="space-y-4 mb-6 pb-6 border-b border-[var(--color-border)]">
            <div className="flex items-center gap-3">
              <Icon name="CalendarIcon" size={20} className="text-[var(--color-text-secondary)]" />
              <div className="flex-1">
                <p className="text-xs text-[var(--color-text-secondary)]">Check-in</p>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  {formatDate(checkIn)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="CalendarIcon" size={20} className="text-[var(--color-text-secondary)]" />
              <div className="flex-1">
                <p className="text-xs text-[var(--color-text-secondary)]">Check-out</p>
                <p className="text-sm font-medium text-[var(--color-text-primary)]">
                  {formatDate(checkOut)}
                </p>
              </div>
            </div>
            {nights > 0 && (
              <div className="flex items-center gap-3">
                <Icon name="ClockIcon" size={20} className="text-[var(--color-text-secondary)]" />
                <div className="flex-1">
                  <p className="text-xs text-[var(--color-text-secondary)]">Duration</p>
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {nights} {nights === 1 ? 'night' : 'nights'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {nights > 0 && (
            <>
              <div className="space-y-3 mb-6 pb-6 border-b border-[var(--color-border)]">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {formatCurrency(basePrice)} × {nights} {nights === 1 ? 'night' : 'nights'}
                  </span>
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {formatCurrency(subtotal)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-secondary)]">GST (5%)</span>
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {formatCurrency(tax)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between mb-6">
                <span className="text-lg font-semibold text-[var(--color-text-primary)]">Total</span>
                <span className="text-2xl font-bold text-[var(--color-accent)]">
                  {formatCurrency(total)}
                </span>
              </div>
            </>
          )}

          <div className="bg-[var(--color-muted)] rounded-lg p-4 flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={20} className="text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Free cancellation up to 48 hours before check-in. Extended stays (30+ nights) qualify for special rates.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <Icon name="HomeIcon" size={48} className="text-[var(--color-muted-foreground)] mx-auto mb-3" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Select an apartment to see pricing details
          </p>
        </div>
      )}
    </div>
  );
}